/**
 * H4b: draws the 12 plains signposts (PLAINS_SIGNS, packages/shared/src/data/plainsSigns.ts) as a pixel wooden post
 * with a small board (placeholder art, joins each camera angle's scenery on the plains only), and shows the board's Thai
 * text over it while the player is within 8 m. Not solid (a post is 1 px wide on the ground; walking passes it).
 * Per frame: a distance test per sign, no allocations.
 */
import { PLAINS_SIGNS, toScreen } from '@nine/shared';
import { kitMap } from '../kit';
import { tag, UI } from '../labels';
import type { Prop } from '../terrain';
import { registerRenderPass } from './registry';

const NEAR = 8;
const W = 20;
const H = 30;

/** One shared sprite: post, board, a pale text mark. */
function signImg(): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d')!;
  g.fillStyle = '#2a1a10'; // outline
  g.fillRect(8, 8, 4, 22);
  g.fillRect(1, 3, 18, 12);
  g.fillStyle = '#7a5230'; // post
  g.fillRect(9, 9, 2, 20);
  g.fillStyle = '#b98a54'; // board
  g.fillRect(2, 4, 16, 10);
  g.fillStyle = '#d9b27a';
  g.fillRect(2, 4, 16, 2);
  g.fillStyle = '#4a2f1a'; // writing
  g.fillRect(5, 8, 10, 1);
  g.fillRect(5, 11, 7, 1);
  return c;
}

registerRenderPass({
  id: 'plainsSigns',
  layer: 'labels',
  order: 111,
  state: () => ({ img: null as HTMLCanvasElement | null }),
  scenery(v, s) {
    if (!kitMap(v.map)) return [];
    s.img ??= signImg();
    const props: Prop[] = [];
    for (const sg of PLAINS_SIGNS) {
      const [vx, vy] = v.view.turn(sg.at[0] + 0.5, sg.at[1] + 0.5);
      const [sx, sy] = toScreen(vx, vy, v.origin);
      props.push({ img: s.img, x: Math.round(sx - W / 2), y: Math.round(sy - H + 2), d: vx + vy });
    }
    return props;
  },
  draw(f) {
    if (f.scene || !f.outdoors || !kitMap(f.world.map)) return;
    const p = f.world.player;
    const { mg, iso, ox, oy, px: PX } = f;
    mg.textAlign = 'center';
    mg.lineJoin = 'round';
    mg.lineWidth = 3.5;
    mg.strokeStyle = 'rgba(12,9,14,.92)';
    for (let i = 0; i < PLAINS_SIGNS.length; i++) {
      const sg = PLAINS_SIGNS[i]!;
      const dx = sg.at[0] + 0.5 - p.x;
      const dy = sg.at[1] + 0.5 - p.y;
      if (dx * dx + dy * dy > NEAR * NEAR) continue;
      const [sx, sy] = iso.proj(sg.at[0] + 0.5, sg.at[1] + 0.5);
      tag(mg, sg.text, (sx + ox) * PX, (sy + oy - H - 4) * PX, `600 13px ${UI}`, '#f0dcae');
    }
  },
});
